import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Residente,
  CreateResidenteRequest,
  UpdateResidenteRequest,
  UnidadResidente,
  AsignarUnidadRequest,
  CerrarAsignacionRequest,
} from '@condo/contracts';
import { ApiRoutes } from '@condo/contracts';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/query-keys';

interface ResidentesListResponse {
  items: Residente[];
}

interface ResidenteDetail {
  residente: Residente;
  asignaciones: UnidadResidente[];
}

export function useResidentes(condoId: string) {
  return useQuery({
    queryKey: queryKeys.residentes(condoId),
    queryFn: () => api.get<ResidentesListResponse>(ApiRoutes.residentes(condoId)),
    enabled: !!condoId,
  });
}

export function useResidente(condoId: string, id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.residente(condoId, id ?? ''),
    queryFn: () => api.get<ResidenteDetail>(ApiRoutes.residenteById(condoId, id!)),
    enabled: !!condoId && !!id,
  });
}

export function useCreateResidente(condoId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateResidenteRequest) =>
      api.post<Residente, CreateResidenteRequest>(ApiRoutes.residentes(condoId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.residentes(condoId) });
    },
  });
}

export function useUpdateResidente(condoId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateResidenteRequest) =>
      api.patch<Residente, UpdateResidenteRequest>(
        ApiRoutes.residenteById(condoId, id),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.residentes(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.residente(condoId, id) });
    },
  });
}

export function useDesactivarResidente(condoId: string, id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<Residente, Record<string, never>>(
        ApiRoutes.residenteDesactivar(condoId, id),
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.residentes(condoId) });
      void qc.invalidateQueries({ queryKey: queryKeys.residente(condoId, id) });
    },
  });
}

export function useAsignarUnidad(condoId: string, residenteId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: AsignarUnidadRequest) =>
      api.post<UnidadResidente, AsignarUnidadRequest>(
        ApiRoutes.residenteAsignarUnidad(condoId, residenteId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.residente(condoId, residenteId) });
      void qc.invalidateQueries({ queryKey: queryKeys.unidades(condoId) });
    },
  });
}

export function useCerrarAsignacion(
  condoId: string,
  residenteId: string,
  asignacionId: string, // = unidadId
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CerrarAsignacionRequest) =>
      api.post<void, CerrarAsignacionRequest>(
        ApiRoutes.residenteCerrarAsignacion(condoId, residenteId, asignacionId),
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.residente(condoId, residenteId) });
      void qc.invalidateQueries({ queryKey: queryKeys.unidades(condoId) });
    },
  });
}
